import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Users, Download } from 'lucide-react';
import { ReportHeader } from '../components/ReportHeader';
import { fmt, fmtDT, StatusPill, Spinner, ErrorBanner, PaginationBar } from '../components/ReportTableKit';
import { ProgressBar } from '../components/SideDrawer';
import LearnerJourneyDrawer from '../components/LearnerJourneyDrawer';

const API = process.env.REACT_APP_BACKEND_URL;
const PER = 100;
const AUDIENCE = { maq: 'MAQ', psh: 'EAS Partner Skilling Hub (PSH)' };
const UNAVAILABLE = 'The Learning Journey enrolments feed is temporarily unavailable. Please try again shortly.';
const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4338ca', fontWeight: 600, fontSize: 13, textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 2 };

function Tiles({ s }) {
  if (!s) return null;
  const t = [['enrolled', 'Enrolled', fmt(s.total_enrolled)], ['completed', 'Completed', fmt(s.completed)],
    ['in-progress', 'In Progress', fmt(s.in_progress)], ['not-started', 'Not Started', fmt(s.not_started)], ['completion', 'Completion', `${s.completion_pct}%`]];
  return (
    <div data-testid="lje-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
      {t.map(([k, l, v]) => (
        <div key={k} className="stat-card" data-testid={`lje-tile-${k}`} style={{ padding: 16 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>{l}</div>
          <div className="stat-value" style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function EnrolTable({ rows, pii, onOpen }) {
  return (
    <div className="table-scroll">
      <table data-testid="lje-table">
        <thead><tr>
          {pii ? <><th>Learner</th><th>Email</th></> : <th>Learner ID</th>}
          <th>Partner</th><th>Country</th><th>Region</th><th>Status</th><th>Progress</th><th>Enrolled</th><th>Completed</th><th>Last Activity</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.student_user_id}-${i}`} data-testid={`lje-row-${i}`}>
              <td style={{ fontSize: 13 }}>
                <button style={linkBtn} onClick={() => onOpen(r)} data-testid={`lje-open-${i}`}>{pii ? (r.learner_name || r.student_user_id) : r.student_user_id}</button>
              </td>
              {pii && <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#3182ce' }}>{r.email || '—'}</td>}
              <td style={{ fontSize: 12 }}>{r.partner_name || '—'}</td>
              <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{r.country || '—'}</td>
              <td style={{ fontSize: 12 }}>{r.region || '—'}</td>
              <td><StatusPill status={r.status} /></td>
              <td><ProgressBar pct={r.progress?.progress_pct} testId={`lje-progress-${i}`} /></td>
              <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(r.enrolled_date)}</td>
              <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(r.completed_date)}</td>
              <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDT(r.last_activity_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LearningJourneyEnrolmentsFeed({ consumer }) {
  const [params, setParams] = useSearchParams();
  const journeyId = params.get('journey') || '';
  const [journeys, setJourneys] = useState([]);
  const [period, setPeriod] = useState({ startDate: '', endDate: '' });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [st, setSt] = useState({ rows: [], total: 0, last: 1, page: 1, pii: false });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [learner, setLearner] = useState(null);
  const [exporting, setExporting] = useState(false);
  const loadSeq = useRef(0);
  const sumSeq = useRef(0);
  const handlePeriod = useCallback(({ startDate, endDate }) => setPeriod({ startDate: startDate || '', endDate: endDate || '' }), []);
  const base = `${API}/api/feeds/${consumer}/learning-journeys/${journeyId}/enrollments`;
  const journeyName = journeys.find(j => String(j.id) === journeyId)?.name;

  useEffect(() => {
    axios.get(`${API}/api/feeds/${consumer}/learning-journeys`, { withCredentials: true })
      .then(r => {
        const list = r.data?.data || [];
        setJourneys(list);
        if (!params.get('journey') && list.length) setParams({ journey: String(list[0].id) }, { replace: true });
      })
      .catch(() => setErr(UNAVAILABLE));
  }, [consumer]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useCallback((extra = {}) => {
    const p = { ...extra };
    if (filters.search) p.search = filters.search;
    if (filters.status) p.status = filters.status;
    if (period.startDate) p.date_from = period.startDate;
    if (period.endDate) p.date_to = period.endDate;
    return p;
  }, [filters, period]);

  const load = useCallback(async (page) => {
    if (!journeyId) return;
    const seq = ++loadSeq.current;
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(base, { params: query({ page, per_page: PER }), withCredentials: true });
      if (seq !== loadSeq.current) return;
      setSt({ rows: r.data?.data || [], total: r.data?.total || 0, last: r.data?.last_page || 1, page: r.data?.current_page || page, pii: r.data?.pii_allowed === true });
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setErr(UNAVAILABLE); setSt({ rows: [], total: 0, last: 1, page: 1, pii: false });
    } finally { if (seq === loadSeq.current) setLoading(false); }
  }, [base, journeyId, query]);

  useEffect(() => { load(1); }, [load]);
  useEffect(() => {
    if (!journeyId) return;
    const seq = ++sumSeq.current;
    setSummary(null);
    const { status, ...p } = query();
    axios.get(`${base}/summary`, { params: p, withCredentials: true })
      .then(r => seq === sumSeq.current && setSummary(r.data))
      .catch(() => seq === sumSeq.current && setSummary(null));
  }, [base, journeyId, query]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${base}/export`, { params: query(), withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `lj-${journeyId}-enrolments-${consumer}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      if (r.headers['x-export-truncated'] === 'true') alert('Export capped at the first 10,000 rows. Narrow the period or filters to export the rest.');
    } catch (e) {
      alert('Export failed — the feed is temporarily unavailable.');
    } finally { setExporting(false); }
  };

  return (
    <div data-testid={`lje-page-${consumer}`}>
      <ReportHeader reportTitle="Learning Journey Enrolments" onChange={handlePeriod} />
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }} data-testid="lje-title"><Users size={22} color="#4338ca" /> Learning Journey Enrolments</h1>
        <p>Live mirror of the per-journey enrolments feed delivered by LevelUp to {AUDIENCE[consumer]}. Period filter applies to enrolment date. Click a learner to see phase and activity progress.</p>
      </div>
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group">
          <label className="filter-label">Journey</label>
          <select className="filter-select" style={{ minWidth: 340 }} value={journeyId} onChange={e => setParams({ journey: e.target.value })} data-testid="lje-journey-select">
            {journeys.map(j => <option key={j.id} value={String(j.id)}>{j.name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select className="filter-select" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} data-testid="lje-status-select">
            <option value="">All statuses</option><option>Completed</option><option>In Progress</option><option>Not Started</option>
          </select>
        </div>
        {st.pii && (
          <div className="filter-group">
            <label className="filter-label">Search</label>
            <input className="filter-input" style={{ minWidth: 240 }} placeholder="Learner name or email…" value={searchInput}
              onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && setFilters(f => ({ ...f, search: searchInput.trim() }))} data-testid="lje-search" />
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {st.pii && <button className="btn btn-primary" onClick={() => setFilters(f => ({ ...f, search: searchInput.trim() }))} data-testid="lje-apply-btn">Apply</button>}
          <button className="btn btn-secondary" onClick={() => { setSearchInput(''); setFilters({ search: '', status: '' }); }} data-testid="lje-reset-btn">Reset</button>
          <button className="btn btn-secondary" onClick={exportCsv} disabled={exporting || !journeyId} data-testid="lje-export-btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>
      <Tiles s={summary} />
      <div className="table-container">
        {loading ? <Spinner /> : err ? <ErrorBanner message={err} testId="lje-error" /> : st.rows.length === 0 ? (
          <div className="empty-state" data-testid="lje-empty"><h3>No enrolments found</h3><p>Try another journey, period or status.</p></div>
        ) : (
          <>
            <div className="table-header">
              <span className="table-title">{journeyName || 'Enrolments'}</span>
              <span className="table-count" style={{ marginLeft: 8 }} data-testid="lje-row-count">({fmt(st.total)} enrolments)</span>
            </div>
            <EnrolTable rows={st.rows} pii={st.pii} onOpen={setLearner} />
            <PaginationBar page={st.page} totalPages={st.last} total={st.total} perPage={PER} onPage={load} testId="lje-pagination" />
          </>
        )}
      </div>
      {learner && <LearnerJourneyDrawer learner={learner} journeyName={journeyName} pii={st.pii} onClose={() => setLearner(null)} />}
    </div>
  );
}
